/**
 * ModelParameters — catalog supported_params-driven parameter controls.
 *
 * Renders only the params a given model actually supports, driven by
 * `TextCapability.supported_params`. Replaces the legacy static-fieldConfig
 * ModelParametersPanel that shows the same hardcoded fields for every model.
 *
 * Phase C of dora#983 model-selection unification.
 */
import { cn } from '@/lib/cn'
import { Switch } from '@/components/ui/Switch'
import { Select } from '@/components/ui/Select'
import type { CatalogModel, TextCapability, ParamSpec } from '@/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ModelParametersProps {
  model: CatalogModel
  /** Current values keyed by param name (from agent config / defaults). */
  values: Record<string, number | boolean | string>
  onChange: (paramName: string, value: number | boolean | string) => void
  disabled?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ')
}

function resolveValue(
  param: ParamSpec,
  values: Record<string, number | boolean | string>,
): number | boolean | string {
  if (param.name in values) return values[param.name]
  if (param.default !== null && param.default !== undefined) return param.default
  if (param.type === 'float' || param.type === 'int') return param.min ?? 0
  if (param.type === 'bool') return false
  return param.choices?.[0] ?? ''
}

// ---------------------------------------------------------------------------
// Per-type control renderers
// ---------------------------------------------------------------------------

interface ControlProps {
  param: ParamSpec
  value: number | boolean | string
  onChange: (paramName: string, value: number | boolean | string) => void
  disabled?: boolean
}

function FloatControl({ param, value, onChange, disabled }: ControlProps) {
  const numVal = typeof value === 'number' ? value : Number(value)
  return (
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={param.min ?? undefined}
        max={param.max ?? undefined}
        step={0.1}
        value={numVal}
        disabled={disabled}
        className={cn(
          'flex-1 h-2 rounded-lg appearance-none cursor-pointer',
          'accent-primary bg-input',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onChange={(e) => onChange(param.name, parseFloat(e.target.value))}
      />
      <span className="w-10 text-right text-sm tabular-nums text-foreground">
        {numVal.toFixed(1)}
      </span>
    </div>
  )
}

function IntControl({ param, value, onChange, disabled }: ControlProps) {
  const numVal = typeof value === 'number' ? value : Number(value)
  return (
    <input
      type="number"
      min={param.min ?? undefined}
      max={param.max ?? undefined}
      value={numVal}
      disabled={disabled}
      className={cn(
        'w-full px-3 py-2 border rounded-lg text-sm transition-colors',
        'bg-card text-foreground border-input',
        'focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-ring focus:ring-ring',
        disabled && 'bg-muted text-muted-foreground cursor-not-allowed',
      )}
      onChange={(e) => onChange(param.name, parseInt(e.target.value, 10))}
    />
  )
}

function BoolControl({ param, value, onChange, disabled }: ControlProps) {
  const boolVal = typeof value === 'boolean' ? value : value === 'true'
  return (
    <Switch
      checked={boolVal}
      onChange={(checked) => onChange(param.name, checked)}
      disabled={disabled}
      ariaLabel={capitalize(param.name)}
    />
  )
}

function EnumControl({ param, value, onChange, disabled }: ControlProps) {
  const choices = param.choices ?? []
  return (
    <Select
      value={String(value)}
      options={choices.map((c) => ({ value: c, label: c }))}
      disabled={disabled}
      onChange={(e) => onChange(param.name, e.target.value)}
    />
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ModelParameters({
  model,
  values,
  onChange,
  disabled,
  className,
}: ModelParametersProps) {
  const capability = model.capability

  if (capability.modality !== 'text') return null

  const textCap = capability as TextCapability
  const activeParams = textCap.supported_params.filter((p) => p.supported !== false)

  if (activeParams.length === 0) return null

  return (
    <div className={cn('space-y-4', className)}>
      {activeParams.map((param) => {
        const value = resolveValue(param, values)
        const controlProps: ControlProps = { param, value, onChange, disabled }

        return (
          <div key={param.name} className="space-y-1.5">
            <label className="block text-sm font-medium text-foreground">
              {capitalize(param.name)}
            </label>
            {param.type === 'float' && <FloatControl {...controlProps} />}
            {param.type === 'int' && <IntControl {...controlProps} />}
            {param.type === 'bool' && <BoolControl {...controlProps} />}
            {param.type === 'enum' && <EnumControl {...controlProps} />}
          </div>
        )
      })}
    </div>
  )
}

ModelParameters.displayName = 'ModelParameters'
